import { tradesmen, messages as messagesApi } from '../api';
import { createDataTable, type Column } from '../components/table';
import { showToast } from '../components/toast';
import { navigate } from '../router';
import type { TradesmanDetail, Message, MaintenanceIssue, QuoteRequest } from '../types';

function fmt(n: number): string {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(n);
}

export async function tradesmanDetailPage(params: Record<string, string>): Promise<HTMLElement> {
    const { id } = params;
    const page = document.createElement('div');

    let detail: TradesmanDetail;
    try {
        detail = await tradesmen.detail(id);
    } catch {
        page.innerHTML = '<div class="text-red-500 p-8">Tradesman not found.</div>';
        return page;
    }

    const { tradesman, messages: msgs, jobs, quote_requests: quotes } = detail;

    // Header
    const header = document.createElement('div');
    header.className = 'page-header';
    header.innerHTML = `
    <div>
      <a href="#/tradesmen" class="text-sm text-gray-400 hover:text-primary-600 mb-1 inline-block">&larr; All Tradesmen</a>
      <h1 class="text-2xl font-bold text-gray-900">${tradesman.name}</h1>
      <div class="flex gap-2 mt-2">
        <span class="badge badge-blue">${tradesman.trade}</span>
        ${tradesman.company ? `<span class="badge badge-gray">${tradesman.company}</span>` : ''}
      </div>
    </div>
  `;
    page.appendChild(header);

    // ── Info ──────────────────────────────────────────────────────────────────────
    const infoSection = document.createElement('div');
    infoSection.className = 'mb-6';
    renderInfo(infoSection, tradesman);
    page.appendChild(infoSection);

    // ── Jobs ──────────────────────────────────────────────────────────────────────
    const jobsSection = document.createElement('div');
    jobsSection.className = 'card mb-6';
    jobsSection.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Jobs <span class="text-gray-400 font-normal text-sm">(${jobs.length})</span></h3>`;
    renderJobs(jobsSection, jobs);
    page.appendChild(jobsSection);

    // ── Quotes ────────────────────────────────────────────────────────────────────
    const quotesSection = document.createElement('div');
    quotesSection.className = 'card mb-6';
    quotesSection.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Quote Requests <span class="text-gray-400 font-normal text-sm">(${quotes.length})</span></h3>`;
    renderQuotes(quotesSection, quotes);
    page.appendChild(quotesSection);

    // ── Messages ──────────────────────────────────────────────────────────────────
    const messagesSection = document.createElement('div');
    messagesSection.className = 'card';
    messagesSection.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Messages &amp; Notes <span class="text-gray-400 font-normal text-sm">(${msgs.length})</span></h3>`;
    const msgContainer = document.createElement('div');
    renderMessages(msgContainer, msgs, id, tradesman.phone);
    messagesSection.appendChild(msgContainer);
    page.appendChild(messagesSection);

    return page;
}

function renderInfo(container: HTMLElement, t: TradesmanDetail['tradesman']): void {
    container.innerHTML = `
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Contact Details</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Name</dt><dd class="font-medium">${t.name}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Trade</dt><dd><span class="badge badge-blue">${t.trade}</span></dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Company</dt><dd class="font-medium">${t.company || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Phone</dt><dd class="font-medium">${t.phone ? `<a href="tel:${t.phone}" class="text-primary-600 hover:underline">${t.phone}</a>` : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Email</dt><dd class="font-medium">${t.email ? `<a href="mailto:${t.email}" class="text-primary-600 hover:underline">${t.email}</a>` : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">ABN</dt><dd class="font-medium">${t.abn || '—'}</dd></div>
        </dl>
        ${t.notes ? `<div class="mt-4 pt-4 border-t border-gray-100"><p class="text-sm text-gray-600 whitespace-pre-wrap">${t.notes}</p></div>` : ''}
      </div>
    </div>
  `;
}

function renderJobs(container: HTMLElement, jobs: MaintenanceIssue[]): void {
    const cols: Column<MaintenanceIssue>[] = [
        { label: 'Title', render: r => r.title || r.description, className: 'max-w-xs truncate' },
        { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_id.substring(0, 8)}...</a>` },
        { label: 'Priority', render: r => `<span class="badge priority-${r.priority}">${r.priority}</span>` },
        { label: 'Status', render: r => `<span class="badge badge-${r.status === 'completed' ? 'green' : r.status === 'in-progress' ? 'yellow' : 'gray'}">${r.status}</span>` },
        { label: 'Actual', render: r => r.actual_amount ? fmt(r.actual_amount) : '—', className: 'text-right' },
    ];
    container.appendChild(createDataTable(cols, jobs, {
        emptyText: 'No jobs assigned.',
        onRowClick: (row) => navigate(`#/properties/${row.property_id}/maintenance/${row.id}`),
    }));
}

function renderQuotes(container: HTMLElement, quotes: QuoteRequest[]): void {
    const cols: Column<QuoteRequest>[] = [
        { label: 'Maintenance', key: 'maintenance_id' },
        { label: 'Status', render: r => `<span class="badge badge-${r.status === 'accepted' ? 'green' : r.status === 'declined' ? 'red' : r.status === 'responded' ? 'blue' : 'yellow'}">${r.status}</span>` },
        { label: 'Channel', render: r => `<span class="badge badge-gray">${r.channel}</span>` },
        { label: 'Quoted', render: r => r.quoted_amount ? fmt(r.quoted_amount) : '—', className: 'text-right' },
        { label: 'Sent', render: r => r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '—' },
    ];
    container.appendChild(createDataTable(cols, quotes, { emptyText: 'No quote requests.' }));
}

function _statusBadge(status: string): string {
    if (!status) return '';
    const colors: Record<string, string> = {
        queued: 'badge-yellow', sent: 'badge-blue', delivered: 'badge-green',
        received: 'badge-green', read: 'badge-green', failed: 'badge-red',
        undelivered: 'badge-red', error: 'badge-red',
    };
    const cls = colors[status] || 'badge-gray';
    return `<span class="badge ${cls} text-[10px] ml-1">${status}</span>`;
}

function renderMessages(container: HTMLElement, msgs: Message[], tradesmanId: string, tradesmanPhone?: string): void {
    container.innerHTML = '';
    const thread = document.createElement('div');
    thread.className = 'space-y-3 mb-6';

    if (msgs.length === 0) {
        thread.innerHTML = '<p class="text-gray-400 text-sm text-center py-8">No messages yet.</p>';
    } else {
        msgs.forEach(m => {
            const isInbound = m.direction === 'inbound';
            const channelIcon = m.channel === 'sms' ? '📱' : '✉️';
            thread.innerHTML += `
        <div class="flex ${isInbound ? 'justify-start' : 'justify-end'}">
          <div class="max-w-[75%] p-3 rounded-lg ${isInbound ? 'bg-gray-100' : 'bg-primary-50 border border-primary-200'}">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-xs font-medium ${isInbound ? 'text-gray-700' : 'text-primary-700'}">${isInbound ? m.from_address : 'You'}</span>
              <span class="text-xs text-gray-400">${channelIcon} ${m.channel}</span>
              ${_statusBadge(m.status)}
            </div>
            ${m.subject ? `<p class="text-xs font-semibold text-gray-600 mb-1">${m.subject}</p>` : ''}
            <p class="text-sm text-gray-800 whitespace-pre-wrap">${m.body}</p>
            <p class="text-xs text-gray-400 mt-1">${new Date(m.created_at).toLocaleString()}</p>
          </div>
        </div>
      `;
        });
    }
    container.appendChild(thread);

    // Channel selector + message form
    const form = document.createElement('form');
    form.className = 'flex gap-2 items-end';
    form.innerHTML = `
    <select name="channel" class="input-field w-24">
      <option value="note">Note</option>
      ${tradesmanPhone ? '<option value="sms">SMS</option>' : ''}
    </select>
    <input type="text" name="body" class="input-field flex-1" placeholder="Type a message..." required>
    <button type="submit" class="btn-primary">Send</button>
  `;
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const channel = fd.get('channel') as string;
        const body = fd.get('body') as string;
        try {
            if (channel === 'sms' && tradesmanPhone) {
                await messagesApi.sendSms('tradesman', tradesmanId, tradesmanPhone, body);
                showToast('SMS sent', 'success');
            } else {
                await messagesApi.create('tradesman', tradesmanId, {
                    body,
                    direction: 'outbound',
                    channel: 'email',
                    related_tradesman_id: tradesmanId,
                });
                showToast('Note added', 'success');
            }
            const updated = await messagesApi.list('tradesman', tradesmanId);
            renderMessages(container, updated, tradesmanId, tradesmanPhone);
        } catch { showToast('Failed to send', 'error'); }
    });
    container.appendChild(form);
}
